import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Building, Save, Upload, ImageIcon, Bot, Copy, Trash2, Plus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { can } from "@/lib/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MCP_URL = `${process.env.REACT_APP_BACKEND_URL}/api/mcp`;

export default function SettingsPage() {
  const { user, workspace, refreshWorkspace } = useAuth();
  const [wsForm, setWsForm] = useState({});
  const [userForm, setUserForm] = useState({});
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("company");

  const [mcpTokens, setMcpTokens] = useState([]);
  const [newToken, setNewToken] = useState(null);
  const [mcpBusy, setMcpBusy] = useState(false);

  const loadMcpTokens = async () => {
    try {
      const res = await axios.get(`${API}/mcp-tokens`);
      setMcpTokens(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (activeTab === "connector") loadMcpTokens();
  }, [activeTab]);

  const generateMcpToken = async () => {
    setMcpBusy(true);
    try {
      const res = await axios.post(`${API}/mcp-tokens`, { name: "Claude Connector" });
      setNewToken(res.data.token);
      await loadMcpTokens();
      toast.success("Token generated — copy it now, it won't be shown again");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate token");
    }
    setMcpBusy(false);
  };

  const revokeMcpToken = async (id) => {
    try {
      await axios.delete(`${API}/mcp-tokens/${id}`);
      await loadMcpTokens();
      toast.success("Token revoked");
    } catch {
      toast.error("Failed to revoke token");
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed — select and copy manually")
    );
  };

  useEffect(() => {
    if (workspace) {
      setWsForm({
        name: workspace.name || "",
        address: workspace.address || "",
        state: workspace.state || "",
        phone: workspace.phone || "",
        gstin: workspace.gstin || "",
        authorized_signatory: workspace.authorized_signatory || "",
        bank_name: workspace.bank_name || "",
        bank_account: workspace.bank_account || "",
        bank_ifsc: workspace.bank_ifsc || "",
        bank_branch: workspace.bank_branch || "",
      });
    }
    if (user) {
      setUserForm({ full_name: user.full_name || "", phone: user.phone || "" });
    }
  }, [workspace, user]);

  const saveWorkspace = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/workspaces/current`, wsForm);
      await refreshWorkspace();
      toast.success("Company settings saved!");
    } catch (err) {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  const [profileUploading, setProfileUploading] = useState(false);
  const uploadProfile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/workspaces/company-profile`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshWorkspace();
      toast.success("Company profile saved — it will be appended to every proposal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload profile");
    }
    setProfileUploading(false);
    e.target.value = "";
  };
  const removeProfile = async () => {
    if (!window.confirm("Remove the company profile? Proposals will no longer include it.")) return;
    try { await axios.delete(`${API}/workspaces/company-profile`); await refreshWorkspace(); toast.success("Company profile removed"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/workspaces/upload-logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      await refreshWorkspace();
      toast.success("Logo uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    }
    setLogoUploading(false);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me`, userForm);
      toast.success("Profile updated!");
    } catch {
      toast.error("Failed to update profile");
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Passwords don't match"); return; }
    if (pwForm.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password changed successfully!");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setSaving(false);
  };

  const isAdmin = can(user, "workspace.edit");
  const tabs = [isAdmin && "company", "profile", "security", can(user, "mcp.tokens") && "connector"].filter(Boolean);

  const inputCls = "w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]";

  return (
    <div className="p-6 max-w-3xl animate-fade-up">
      <h1 className="font-heading font-bold text-2xl text-[#0A1128] mb-6">Settings</h1>

      {/* Tab Nav */}
      <div className="flex gap-1 bg-white border border-[#E2E8F0] rounded p-1 mb-6 w-fit">
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2 text-sm rounded capitalize transition-colors ${activeTab === t ? "bg-[#F5F8F5] text-[#1B2B22] font-semibold" : "text-[#64748B] hover:text-[#0A1128]"}`}
            data-testid={`settings-tab-${t}`}
          >
            {t === "company" ? "Company Profile" : t === "profile" ? "My Profile" : t === "connector" ? "Claude / MCP" : "Security"}
          </button>
        ))}
      </div>

      {/* Company Settings */}
      {activeTab === "company" && isAdmin && (
        <div className="bg-white border border-[#E2E8F0] rounded p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Building size={18} className="text-[#FFB703]" />
            <h2 className="font-heading font-semibold text-base text-[#0A1128]">Company Profile</h2>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Company Name</label>
              <input type="text" value={wsForm.name || ""} onChange={e => setWsForm(p => ({ ...p, name: e.target.value }))} className={inputCls} data-testid="settings-company-name" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Company Logo</label>
              <div className="flex items-center gap-4">
                {workspace?.logo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${workspace.logo_url}`} alt="company logo" className="h-14 w-auto object-contain border border-[#E2E8F0] rounded p-1 bg-white" />
                ) : (
                  <div className="h-14 w-20 border border-dashed border-[#E2E8F0] rounded flex items-center justify-center bg-[#F8FAFC]">
                    <ImageIcon size={20} className="text-[#94A3B8]" />
                  </div>
                )}
                <div>
                  <label className="cursor-pointer flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#FFB703] px-4 py-2 rounded text-sm text-[#64748B] hover:text-[#0A1128] transition-colors">
                    <Upload size={14} />
                    {logoUploading ? "Uploading..." : workspace?.logo_url ? "Change Logo" : "Upload Logo"}
                    <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" disabled={logoUploading} />
                  </label>
                  <p className="text-[10px] text-[#94A3B8] mt-1">PNG, JPG or SVG. Appears on invoices, POs and sidebar.</p>
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Company Profile (PDF)</label>
              <div className="flex flex-wrap items-center gap-3">
                {workspace?.company_profile_url ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${workspace.company_profile_url}`} target="_blank" rel="noreferrer" className="text-sm text-[#2F7D52] underline" data-testid="settings-profile-link">
                    {workspace.company_profile_name || "company-profile.pdf"}{workspace.company_profile_pages ? ` · ${workspace.company_profile_pages} pages` : ""}
                  </a>
                ) : <span className="text-sm text-[#94A3B8]">Not uploaded</span>}
                <label className="cursor-pointer flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#FFB703] px-4 py-2 rounded text-sm">
                  <Upload size={14} />
                  {profileUploading ? "Uploading..." : workspace?.company_profile_url ? "Replace" : "Upload PDF"}
                  <input type="file" accept="application/pdf" onChange={uploadProfile} className="hidden" disabled={profileUploading} data-testid="settings-profile-input" />
                </label>
                {workspace?.company_profile_url && <button onClick={removeProfile} className="text-xs text-red-600 hover:underline">Remove</button>}
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">Upload once; appended to every customer proposal generated from the CRM (you can untick it per proposal).</p>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Registered Address</label>
              <textarea value={wsForm.address || ""} onChange={e => setWsForm(p => ({ ...p, address: e.target.value }))} rows={2} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">State</label>
              <input type="text" value={wsForm.state || ""} onChange={e => setWsForm(p => ({ ...p, state: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Phone</label>
              <input type="tel" value={wsForm.phone || ""} onChange={e => setWsForm(p => ({ ...p, phone: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">GSTIN</label>
              <input type="text" value={wsForm.gstin || ""} onChange={e => setWsForm(p => ({ ...p, gstin: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Authorized Signatory</label>
              <input type="text" value={wsForm.authorized_signatory || ""} onChange={e => setWsForm(p => ({ ...p, authorized_signatory: e.target.value }))} className={inputCls} />
            </div>
          </div>

          <div className="border-t border-[#E2E8F0] pt-5">
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-3">Bank Details (for PI documents)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Bank Name</label>
                <input type="text" value={wsForm.bank_name || ""} onChange={e => setWsForm(p => ({ ...p, bank_name: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Account Number</label>
                <input type="text" value={wsForm.bank_account || ""} onChange={e => setWsForm(p => ({ ...p, bank_account: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">IFSC Code</label>
                <input type="text" value={wsForm.bank_ifsc || ""} onChange={e => setWsForm(p => ({ ...p, bank_ifsc: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Branch</label>
                <input type="text" value={wsForm.bank_branch || ""} onChange={e => setWsForm(p => ({ ...p, bank_branch: e.target.value }))} className={inputCls} />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={saveWorkspace} disabled={saving} className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2.5 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] disabled:opacity-60" data-testid="save-company-btn">
              <Save size={14} />{saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* Profile */}
      {activeTab === "profile" && (
        <div className="bg-white border border-[#E2E8F0] rounded p-6 space-y-4">
          <h2 className="font-heading font-semibold text-base text-[#0A1128] mb-4">My Profile</h2>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Full Name</label>
            <input type="text" value={userForm.full_name || ""} onChange={e => setUserForm(p => ({ ...p, full_name: e.target.value }))} className={inputCls} data-testid="settings-full-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Email</label>
            <input type="email" value={user?.email || ""} disabled className={`${inputCls} bg-[#F8FAFC] text-[#64748B]`} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Phone</label>
            <input type="tel" value={userForm.phone || ""} onChange={e => setUserForm(p => ({ ...p, phone: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Role</label>
            <input type="text" value={user?.role?.replace("_", " ")} disabled className={`${inputCls} bg-[#F8FAFC] text-[#64748B] capitalize`} />
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={saveProfile} disabled={saving} className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2.5 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] disabled:opacity-60" data-testid="save-profile-btn">
              <Save size={14} />{saving ? "Saving..." : "Save Profile"}
            </button>
          </div>
        </div>
      )}

      {/* Security */}
      {activeTab === "security" && (
        <div className="bg-white border border-[#E2E8F0] rounded p-6 space-y-4">
          <h2 className="font-heading font-semibold text-base text-[#0A1128] mb-4">Change Password</h2>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Current Password</label>
            <input type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">New Password</label>
            <input type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Confirm New Password</label>
            <input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} className={inputCls} />
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={changePassword} disabled={saving} className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2.5 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] disabled:opacity-60">
              <Save size={14} />{saving ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      )}

      {/* Claude / MCP Connector */}
      {activeTab === "connector" && (
        <div className="space-y-6" data-testid="mcp-connector-panel">
          <div className="bg-white border border-[#E2E8F0] rounded p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Bot size={18} className="text-[#FFB703]" />
              <h2 className="font-heading font-semibold text-base text-[#0A1128]">Connect to Claude (MCP)</h2>
            </div>
            <p className="text-sm text-[#64748B] leading-relaxed">
              Connect this workspace to <span className="font-semibold text-[#0A1128]">Claude</span> as a custom connector.
              Once connected, Claude can read and manage your projects &amp; CRM leads on your behalf. Add the URL below
              in Claude → <span className="font-medium">Settings → Connectors → Add custom connector</span>, then paste
              a generated token under <span className="font-medium">Advanced → Request headers</span> as{" "}
              <code className="px-1 py-0.5 bg-[#F1F5F9] rounded text-[#0A1128]">x-api-key</code>.
              <br />
              <span className="text-[#94A3B8]">Prefer OAuth? Add just the URL with no header — if your Claude tier
              requires it, Claude opens a SunBiz sign-in page to authorize automatically.</span>
            </p>

            {/* Connector URL */}
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Connector URL</label>
              <div className="flex items-center gap-2">
                <input readOnly value={MCP_URL} className={`${inputCls} font-mono text-xs bg-[#F8FAFC]`} data-testid="mcp-url-input" />
                <button onClick={() => copyText(MCP_URL, "URL")} className="shrink-0 flex items-center gap-1.5 bg-[#F1F5F9] hover:bg-[#E2E8F0] border border-[#E2E8F0] px-3 py-2.5 rounded text-sm text-[#0A1128] transition-colors" data-testid="copy-mcp-url-btn">
                  <Copy size={14} /> Copy
                </button>
              </div>
              <p className="text-[11px] text-[#94A3B8] mt-1.5">
                ⚠️ This works only on your <span className="font-medium">deployed</span> site (e.g. https://yourdomain.com/api/mcp).
                Preview URLs may sleep or change.
              </p>
            </div>

            {/* Generate token */}
            <div className="border-t border-[#E2E8F0] pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <KeyRound size={15} className="text-[#64748B]" />
                  <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Access Tokens</p>
                </div>
                <button onClick={generateMcpToken} disabled={mcpBusy} className="flex items-center gap-1.5 bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] disabled:opacity-60" data-testid="generate-mcp-token-btn">
                  <Plus size={14} />{mcpBusy ? "Generating..." : "Generate Token"}
                </button>
              </div>

              {newToken && (
                <div className="bg-[#FFF8E7] border border-[#FFB703]/50 rounded p-4 mb-4" data-testid="new-mcp-token">
                  <p className="text-xs font-semibold text-[#92700A] mb-2">Copy this token now — it will not be shown again.</p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={newToken} className={`${inputCls} font-mono text-xs bg-white`} data-testid="new-mcp-token-value" />
                    <button onClick={() => copyText(newToken, "Token")} className="shrink-0 flex items-center gap-1.5 bg-[#0A1128] text-white px-3 py-2.5 rounded text-sm hover:bg-[#1B2B22] transition-colors" data-testid="copy-mcp-token-btn">
                      <Copy size={14} /> Copy
                    </button>
                  </div>
                </div>
              )}

              {mcpTokens.length === 0 ? (
                <p className="text-sm text-[#94A3B8]">No tokens yet. Generate one to connect Claude.</p>
              ) : (
                <div className="space-y-2" data-testid="mcp-token-list">
                  {mcpTokens.map((t) => (
                    <div key={t.id} className="flex items-center justify-between border border-[#E2E8F0] rounded px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-[#0A1128]">{t.name}</p>
                        <p className="text-[11px] text-[#94A3B8] font-mono">
                          {t.token_prefix}••••••  ·  created {new Date(t.created_at).toLocaleDateString("en-IN")}
                          {t.last_used_at ? `  ·  last used ${new Date(t.last_used_at).toLocaleDateString("en-IN")}` : "  ·  never used"}
                        </p>
                      </div>
                      <button onClick={() => revokeMcpToken(t.id)} className="flex items-center gap-1.5 text-[#DC2626] hover:bg-[#FEF2F2] px-3 py-1.5 rounded text-sm transition-colors" data-testid={`revoke-mcp-token-${t.id}`}>
                        <Trash2 size={14} /> Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
